import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  CURRENCIES,
  HIGHLEVEL_SCOPES,
  INDUSTRIES,
  TIME_ZONES,
  initials,
  type ConnectionTestResponse,
  type Industry, IMPORT_DAY_OPTIONS, type ImportDays } from '@coachrex/shared';
import { useAdminCompany, useCreateCompany, useFixConnection, useTestConnection } from '../lib/api';
import { ErrorBox, Pills } from '../components/ui';

const STEPS = ['Company', 'Connect HighLevel', 'Managers and reps', 'Review'] as const;

const HOW_TO: [string, string][] = [
  ['Open the sub-account', "Log in to HighLevel and switch to this company's sub-account (not the agency view)."],
  ['Go to Private Integrations', 'Settings → Private Integrations → Create new integration. If you don’t see it, turn it on under Labs.'],
  ['Name it', 'Call it "Coach Lex" so the team knows what it is.'],
  ['Tick the scopes', 'Select the scopes listed below. Coach Lex only reads, except for adding notes and tags.'],
  ['Copy the token', 'Click Create, then copy the token. It starts with pit- and HighLevel only shows it once.'],
  ['Find the Location ID', 'Settings → Business Profile, top right of General Information. It’s also in the URL after /location/.'],
];

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Add company (4 steps) and Fix connection (the HighLevel step only, for an existing company). */
export function CompanyWizard({ mode }: { mode: 'add' | 'fix' }) {
  const { id } = useParams();
  const fixing = useAdminCompany(mode === 'fix' ? id : undefined);
  const navigate = useNavigate();

  const [step, setStep] = useState(mode === 'fix' ? 2 : 1);
  const [name, setName] = useState('');
  const [industry, setIndustry] = useState<Industry>('ADU / construction');
  const [timeZone, setTimeZone] = useState<string>(TIME_ZONES[0].value);
  const [currency, setCurrency] = useState<string>('USD');
  const [importDays, setImportDays] = useState<ImportDays>(180);
  const [locationId, setLocationId] = useState('');
  const [token, setToken] = useState('');
  const [result, setResult] = useState<ConnectionTestResponse | null>(null);
  const [managers, setManagers] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [copied, setCopied] = useState(false);

  const test = useTestConnection();
  const create = useCreateCompany();
  const fix = useFixConnection(id ?? '');
  const saving = create.isPending || fix.isPending;
  const saveError = create.error ?? fix.error;

  const tested = result?.ok === true;
  // Editing the credentials invalidates an earlier passing test.
  const edit = (set: (v: string) => void) => (v: string) => {
    set(v);
    setResult(null);
  };

  const runTest = () => {
    const tok = token.trim();
    if (!/^pit-/.test(tok)) return setResult({ ok: false, error: "That doesn't look like a Private Integration token. It should start with pit-." });
    test.mutate({ locationId: locationId.trim(), token: tok }, { onSuccess: setResult, onError: (e) => setResult({ ok: false, error: e.message }) });
  };

  const addManager = () => {
    const e = draft.trim().toLowerCase();
    if (!EMAIL.test(e)) return;
    if (!managers.includes(e)) setManagers([...managers, e]);
    setDraft('');
  };

  const canNext = step === 1 ? name.trim().length > 0 : step === 2 ? tested : true;

  const finish = () => {
    if (mode === 'fix') {
      fix.mutate({ locationId: locationId.trim(), token: token.trim() }, { onSuccess: () => navigate('/admin') });
      return;
    }
    create.mutate(
      { name: name.trim(), industry, timeZone, currency, locationId: locationId.trim(), token: token.trim(), extraManagers: managers, excludedUserIds: [...excluded], importDays },
      { onSuccess: (c) => navigate('/admin', { state: { added: c.name } }) },
    );
  };

  const next = () => {
    if (!canNext || saving) return;
    if (mode === 'fix' || step === 4) return finish();
    // Prefill the time zone from HighLevel when the sub-account's zone is one we offer.
    if (step === 2 && result?.timeZone && TIME_ZONES.some((z) => z.value === result.timeZone)) setTimeZone(result.timeZone);
    setStep(step + 1);
  };
  const back = () => (step === 1 || mode === 'fix' ? navigate('/admin') : setStep(step - 1));

  const users = result?.users ?? [];
  const tzLabel = TIME_ZONES.find((z) => z.value === timeZone)?.label ?? timeZone;

  return (
    <div className="page">
      <Link to="/admin" style={{ fontSize: 13, alignSelf: 'flex-start' }}>
        ← All companies
      </Link>
      <h1 className="h1">{mode === 'fix' ? `Fix connection${fixing.data ? `: ${fixing.data.name}` : ''}` : 'Add a company'}</h1>
      <div className="wizard">
        <ol className="wiz-steps">
          {STEPS.map((label, i) => {
            const n = i + 1;
            if (mode === 'fix' && n !== 2) return null;
            const state = n < step ? 'is-done' : n === step ? 'is-on' : '';
            return (
              <li key={label} className={`wiz-step ${state}`} aria-current={n === step ? 'step' : undefined}>
                <span className="wiz-step__n">{n < step ? '✓' : n}</span>
                {label}
              </li>
            );
          })}
        </ol>

        <section className="wiz-card">
          {step === 1 && (
            <>
              <div className="wiz-title">
                <b>Company details</b>
                <span>Basic info for the new workspace.</span>
              </div>
              <label className="field" style={{ maxWidth: 480 }}>
                <span>Company name</span>
                <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. ADU Resource Center" autoFocus />
              </label>
              <div className="field">
                <span className="field-label">Industry</span>
                <Pills options={INDUSTRIES.map((v) => ({ value: v, label: v }))} value={industry} onChange={setIndustry} />
                <span className="help">Sets the starting templates for Lead scoring and Journey setup. Everything can be edited later.</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14, maxWidth: 480 }}>
                <label className="field">
                  <span>Time zone</span>
                  <select className="input" value={timeZone} onChange={(e) => setTimeZone(e.target.value)}>
                    {TIME_ZONES.map((z) => (
                      <option key={z.value} value={z.value}>
                        {z.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span>Currency</span>
                  <select className="input" value={currency} onChange={(e) => setCurrency(e.target.value)}>
                    {CURRENCIES.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span>History to import</span>
                  <select className="input" value={importDays} onChange={(e) => setImportDays(Number(e.target.value) as ImportDays)}>
                    {IMPORT_DAY_OPTIONS.map((o) => (
                      <option key={o.days} value={o.days}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="wiz-title">
                <b>Connect HighLevel</b>
                <span>
                  {mode === 'fix'
                    ? 'Create a new Private Integration token in the same sub-account and paste it here. The old token stops being used.'
                    : "Create a Private Integration token in the company's HighLevel sub-account, then paste it here with the Location ID."}
                </span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.25fr) minmax(0, 1fr)', gap: 20, alignItems: 'start' }}>
                <div className="howto">
                  <span style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>How to get the token</span>
                  <ol>
                    {HOW_TO.map(([t, d], i) => (
                      <li key={t}>
                        <span className="howto__n">{i + 1}</span>
                        <span style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                          <span className="howto__t">{t}</span>
                          <span className="howto__d">{d}</span>
                        </span>
                      </li>
                    ))}
                  </ol>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 12.5, fontWeight: 600 }}>Scopes to tick</span>
                      <button
                        className="link"
                        style={{ fontSize: 12 }}
                        onClick={() => {
                          void navigator.clipboard?.writeText(HIGHLEVEL_SCOPES.join('\n')).then(() => {
                            setCopied(true);
                            setTimeout(() => setCopied(false), 2000);
                          });
                        }}
                      >
                        {copied ? 'Copied' : 'Copy list'}
                      </button>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                      {HIGHLEVEL_SCOPES.map((s) => (
                        <span key={s} className="scope">
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <label className="field">
                    <span>Location ID</span>
                    <input
                      className="input mono"
                      value={locationId}
                      onChange={(e) => edit(setLocationId)(e.target.value)}
                      placeholder={fixing.data?.locationId ?? 'e.g. ve9EPM428h8vShlRW1KT'}
                      spellCheck={false}
                      autoComplete="off"
                    />
                    <span className="help">Settings → Business Profile, or the part of the URL after /location/</span>
                  </label>
                  <label className="field">
                    <span>Private Integration token</span>
                    <input
                      className="input mono"
                      type="password"
                      value={token}
                      onChange={(e) => edit(setToken)(e.target.value)}
                      placeholder="pit-…"
                      autoComplete="new-password"
                      spellCheck={false}
                    />
                    <span className="help">Stored encrypted. Coach Lex never shows it again.</span>
                  </label>
                  <div>
                    <button className="btn btn--accent" disabled={!locationId.trim() || !token.trim() || test.isPending} onClick={runTest}>
                      {test.isPending ? 'Testing…' : tested ? 'Test again' : 'Test connection'}
                    </button>
                  </div>
                  {result && !result.ok && <span className="test-err">{result.error}</span>}
                  {tested && (
                    <div className="test-ok">
                      <span className="test-ok__title">Connected to {result.subAccount}</span>
                      {(
                        [
                          ['Sub-account found', result.subAccount ?? ''],
                          ['Contacts', result.counts?.contacts],
                          ['Conversations (calls, texts, emails)', result.counts?.conversations],
                          ['Calendars', result.counts?.calendars],
                          ['Tags', result.counts?.tags],
                          ['Users', result.counts?.users],
                        ] as const
                      ).map(([label, v]) => (
                        <span key={label} className="test-ok__row">
                          <span>
                            <b>✓</b> {label}
                          </span>
                          <span style={{ color: 'var(--faint)' }}>{typeof v === 'number' ? v.toLocaleString('en-US') : v}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <div className="wiz-title">
                <b>Managers and reps</b>
                <span>
                  People sign in with Google. Anyone whose Google email matches an Admin user in this HighLevel account becomes a manager automatically. Reps come from HighLevel and don't need to sign in.
                </span>
              </div>
              <div className="field" style={{ maxWidth: 520, gap: 8 }}>
                <span className="field-label">Extra managers (optional)</span>
                <span className="help" style={{ marginTop: -4 }}>
                  Only for people who aren't HighLevel users. They sign in with Google using this email.
                </span>
                {managers.map((m) => (
                  <div key={m} className="email-row">
                    <span>{m}</span>
                    <button aria-label={`Remove ${m}`} onClick={() => setManagers(managers.filter((x) => x !== m))}>
                      ×
                    </button>
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    className="input"
                    style={{ flex: 1, minWidth: 0, fontSize: 13.5 }}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addManager()}
                    placeholder="name@company.com"
                    type="email"
                  />
                  <button className="btn btn--primary" style={{ padding: '8px 14px', fontSize: 12.5 }} disabled={!EMAIL.test(draft.trim())} onClick={addManager}>
                    Add
                  </button>
                </div>
              </div>
              <div className="field" style={{ maxWidth: 520 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span className="field-label">People found in HighLevel</span>
                  <span className="help">Untick anyone who isn't in sales</span>
                </div>
                {users.length === 0 && <span className="help">No users found in this sub-account.</span>}
                {users.map((u) => {
                  const on = !excluded.has(u.crmId);
                  return (
                    <div key={u.crmId} className="person" style={{ opacity: on ? 1 : 0.5 }}>
                      <span className="person__avatar">{initials(u.name)}</span>
                      <span style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <span style={{ fontWeight: 500 }}>{u.name}</span>
                        <span style={{ fontSize: 12, color: 'var(--faint)' }}>{u.role === 'admin' ? 'HighLevel admin · manager' : 'HighLevel user · rep'}</span>
                      </span>
                      <button
                        className={`toggle${on ? ' is-on' : ''}`}
                        role="switch"
                        aria-checked={on}
                        aria-label={`Include ${u.name}`}
                        onClick={() => {
                          const s = new Set(excluded);
                          if (on) s.add(u.crmId);
                          else s.delete(u.crmId);
                          setExcluded(s);
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <div className="wiz-title">
                <b>Review</b>
                <span>Coach Lex will import calls, texts, emails and tags from HighLevel. This usually takes 10 minutes to an hour, depending on how far back.</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', maxWidth: 560 }}>
                {(
                  [
                    ['Company', name.trim()],
                    ['Industry', `${industry} (starting templates for Lead scoring and Journey setup)`],
                    ['Time zone', tzLabel],
                    ['Currency', currency],
                    ['History to import', IMPORT_DAY_OPTIONS.find((o) => o.days === importDays)?.label ?? ''],
                    ['HighLevel', `Connected to ${result?.subAccount ?? ''} · Location ${mask(locationId.trim())}`],
                    ['Extra managers', managers.length ? managers.join(', ') : 'None. HighLevel admins become managers automatically.'],
                    ['People included', `${users.length - excluded.size} of ${users.length}`],
                  ] as const
                ).map(([k, v]) => (
                  <div key={k} className="review-row">
                    <span>{k}</span>
                    <span>{v}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          {saveError && <ErrorBox error={saveError} />}

          <div className="wiz-foot">
            <button className="btn btn--secondary" onClick={back}>
              {step === 1 || mode === 'fix' ? 'Cancel' : 'Back'}
            </button>
            <button className="btn btn--primary" style={{ padding: '9px 18px' }} disabled={!canNext || saving} onClick={next}>
              {saving ? 'Saving…' : mode === 'fix' ? 'Save connection' : step === 4 ? 'Create company' : 'Continue'}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}

function mask(id: string): string {
  return id.length > 8 ? `${id.slice(0, 4)}…${id.slice(-3)}` : id;
}
